import React, { useCallback, useState, lazy, Suspense, useMemo, useEffect, useRef } from "react"
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter"
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism"
import json from "react-syntax-highlighter/dist/esm/languages/prism/json"
import ReactMarkdown from "react-markdown"
import { debounce } from "@mui/material"
import fileStyles from "./FilePanel.module.css" // 导入文件面板样式模块

SyntaxHighlighter.registerLanguage("json", json)

const CommentInput = React.memo(({ index, comment, onCommentChange }) => {
  const [inputValue, setInputValue] = useState(comment)

  const debouncedOnChange = useCallback(
    debounce((index, value) => {
      onCommentChange(index, value)
    }, 1000),
    [onCommentChange]
  ) // 500ms防抖

  const handleChange = (e) => {
    const value = e.target.value
    setInputValue(value)
    debouncedOnChange(index, value)
  }

  return (
    <textarea
      value={inputValue}
      onChange={handleChange}
      placeholder="添加评论..."
    />
  )
})

// 检测连续的表格行，返回表格组的起始和结束索引
const detectTableGroups = (content) => {
  const tableGroups = []
  let inTable = false
  let startIndex = -1
  let lastTableEndIndex = -1

  // 表格标记正则: 检测至少有一个 | 符号的行
  const tableRowRegex = /^\s*\|.*\|\s*$/

  for (let i = 0; i < content.length; i++) {
    const line = content[i].content
    const isTableRow = tableRowRegex.test(line)

    if (isTableRow && !inTable) {
      // 开始新表格
      inTable = true
      startIndex = i
    } else if (!isTableRow && inTable) {
      // 表格结束
      inTable = false
      // 只有当表格至少有2行时才视为有效表格（包括表头和分隔符行）
      if (i - startIndex >= 2) {
        tableGroups.push({ start: startIndex, end: i - 1 })
        lastTableEndIndex = i - 1
      }
    }
  }

  // 处理文件末尾的表格
  if (inTable && content.length - startIndex >= 2) {
    tableGroups.push({ start: startIndex, end: content.length - 1 })
  }

  return tableGroups
}

// 合并表格内容，同时保留每行的类型信息
const mergeTableContent = (lines) => {
  // 返回包含内容和类型的对象数组，而不是简单的字符串
  return lines.map(line => ({
    content: line.content.trim(),
    type: line.type
  }))
}

// 解析表格内容为结构化数据
const parseTable = (tableLines) => {
  // 确定表头行、分隔行和数据行
  let headerRow = null
  let separatorRow = null
  const dataRows = []
  
  // 查找表头和分隔行
  for (let i = 0; i < tableLines.length; i++) {
    const line = tableLines[i].content
    
    // 检查是否是分隔行 (只包含 |、-、: 字符)
    if (/^\s*\|[\s\-:\|]+\|\s*$/.test(line)) {
      separatorRow = i
      headerRow = i > 0 ? i - 1 : null
      break
    }
  }
  
  // 如果找到了分隔行，将其余行视为数据行
  if (separatorRow !== null) {
    for (let i = 0; i < tableLines.length; i++) {
      if (i !== headerRow && i !== separatorRow) {
        dataRows.push(i)
      }
    }
  }
  
  // 解析表格单元格
  const extractCells = (line) => {
    // 移除行开头和结尾的 | 并按 | 分割
    return line.trim()
      .substring(1, line.length - 1)
      .split('|')
      .map(cell => cell.trim())
  }
  
  // 构建表格数据
  const tableData = {
    hasHeader: headerRow !== null,
    headers: headerRow !== null ? extractCells(tableLines[headerRow].content) : [],
    headerType: headerRow !== null ? tableLines[headerRow].type : 'unchanged',
    rows: dataRows.map(i => ({
      cells: extractCells(tableLines[i].content),
      type: tableLines[i].type
    }))
  }
  
  return tableData
}

// 渲染表格组件
const TableRenderer = ({ tableData }) => {
  // 表格容器样式
  const tableContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    margin: '12px 0',
    overflow: 'auto'
  }
  
  // 表格样式
  const tableStyle = {
    borderCollapse: 'collapse',
    width: 'auto',
    maxWidth: '100%',
    border: '1px solid #ddd',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    borderRadius: '4px'
  }
  
  // 表头样式
  const getHeaderRowStyle = (type) => {
    return {
      backgroundColor: type === 'added' ? 'rgba(40, 167, 69, 0.15)' : 
                      type === 'deleted' ? 'rgba(220, 53, 69, 0.15)' : 
                      '#f8f9fa',
      fontWeight: 'bold'
    }
  }
  
  // 数据行样式
  const getRowStyle = (type) => {
    return {
      backgroundColor: type === 'added' ? 'rgba(40, 167, 69, 0.08)' : 
                      type === 'deleted' ? 'rgba(220, 53, 69, 0.08)' : 
                      '#ffffff'
    }
  }
  
  // 单元格样式
  const cellStyle = {
    padding: '6px 10px',
    border: '1px solid #ddd',
    fontSize: '0.95em'
  }
  
  // 表头单元格样式
  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 'bold',
    textAlign: 'left'
  }
  
  return (
    <div style={tableContainerStyle}>
      <table style={tableStyle}>
        {tableData.hasHeader && (
          <thead>
            <tr style={getHeaderRowStyle(tableData.headerType)}>
              {tableData.headers.map((cell, j) => (
                <th key={j} style={headerCellStyle}>{cell}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {tableData.rows.map((row, i) => (
            <tr key={i} style={getRowStyle(row.type)}>
              {row.cells.map((cell, j) => (
                <td key={j} style={cellStyle}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// 添加共享辅助函数，放在文件适当位置
// 建议放在EnhancedJsonRenderer组件前面，作为全局辅助函数

/**
 * 处理Mermaid图表内容中的转义字符和特殊语法
 * @param {string} chartContent - 提取的Mermaid图表内容
 * @returns {string} 处理后的图表内容
 */
const processMermaidContent = (chartContent) => {
  if (!chartContent) return "";
  
  // 处理常见的转义字符和格式化问题
  let processedContent = chartContent
    .replace(/\\n/g, "\n")
    .replace(/\\"/g, '"')
    .replace(/"""/g, '"')
    .replace(/\\\\/g, "\\")
    .replace(/\\'/g, "'")
    .replace(/'/g, '"')
    .replace(/END/g, "end")
    // 添加对箭头符号和其他符号的转义处理
    .replace(/\\-\\-\\>/g, "-->")
    .replace(/\\-\\->/g, "-->")
    .replace(/--\\>/g, "-->")
    .replace(/\\-->/g, "-->")
    // 添加对quadrantChart特有语法的支持 (仅基础处理)
    .replace(/quadrantChart\\n/g, "quadrantChart\n")
    .replace(/x-axis\\s+/g, "x-axis ")
    .replace(/y-axis\\s+/g, "y-axis ")
    .replace(/quadrant-[1-4]\\s+/g, match => match.replace("\\", ""))
    // 确保引号格式正确
    .replace(/\\"/g, '"')
    .trim();
  
  // 移除：不再在此处调用 formatQuadrantChart，最终格式化将在渲染前进行
  // if (processedContent.includes("quadrantChart")) {
  //   return formatQuadrantChart(processedContent);
  // }
  
  return processedContent;
};

// 添加专门处理quadrantChart格式的辅助函数
const formatQuadrantChart = (content) => {
  // 如果已经是格式良好的quadrantChart，则不需要额外处理
  if (content.startsWith("quadrantChart\n") && 
      (content.includes("\n    title ") || content.includes("\n    x-axis "))) {
    return content;
  }
  
  // 确保以quadrantChart开头
  let result = content;
  if (!result.startsWith("quadrantChart")) {
    result = "quadrantChart\n" + result;
  } else if (!result.startsWith("quadrantChart\n")) {
    result = "quadrantChart\n" + result.substring(12).trim();
  }
  
  // 提取和格式化关键元素
  const keyElements = [
    "title", "x-axis", "y-axis", 
    "quadrant-1", "quadrant-2", "quadrant-3", "quadrant-4"
  ];
  
  // 使用正则表达式识别和格式化各个元素
  keyElements.forEach(element => {
    const regex = new RegExp(`(^|\\s)${element}\\s+"([^"]+)"`, 'g');
    result = result.replace(regex, `\n    ${element} "$2"`);
  });
  
  // 处理数据点
  result = result.replace(/\s*"([^"]+)":\s*\[([^,]+),\s*([^\]]+)\]/g, '\n    "$1": [$2, $3]');
  
  // 确保格式正确，移除多余空白行
  result = result.replace(/\n\s*\n/g, '\n').trim();
  
  return result;
};

// 修改 EnhancedJsonRenderer 组件为默认导出
const EnhancedJsonRenderer = ({
  content,
  selectedLines,
  onLineClick,
  comments,
  onCommentChange,
  fileName,
}) => {
  // 使用useRef替代useState跟踪已处理的Mermaid图表行索引
  const processedMermaidIndicesRef = useRef(new Set());
  
  // 在内容变化时重置处理状态
  useEffect(() => {
    processedMermaidIndicesRef.current = new Set();
  }, [content]);

  // 创建一个组件实例的唯一标识符，用于跟踪渲染周期
  const instanceId = React.useRef(Math.random().toString(36).substring(2, 9)).current;
  
  // 缓存处理过的内容，避免重复渲染时的计算开销
  const processedContent = useMemo(() => {
    //console.log(`[EnhancedJsonRenderer] 处理内容 (${instanceId})`);
    return content;
  }, [content, instanceId]);

  // 预先计算内容类型
  const isArrayWithType = useMemo(() => {
    return Array.isArray(processedContent) && 
           processedContent.length > 0 && 
           "type" in processedContent[0];
  }, [processedContent]);

  // 如果是数组类型，预先计算表格组
  const tableGroups = useMemo(() => {
    if (isArrayWithType) {
      return detectTableGroups(processedContent);
    }
    return [];
  }, [isArrayWithType, processedContent]);

  // 使用Lazy加载LazyMermaidRenderer
  const LazyMermaidRenderer = lazy(() => import('./components/LazyMermaidRenderer'));
  
  // 在组件渲染时记录
  useEffect(() => {
    //console.log(`[EnhancedJsonRenderer] 渲染 (${instanceId})`);
    
    return () => {
     // console.log(`[EnhancedJsonRenderer] 卸载 (${instanceId})`);
    };
  }, [instanceId]);
  
  // 如果内容是差异数据
  if (isArrayWithType) {
    // 如果没有表格，使用原来的渲染逻辑
    if (tableGroups.length === 0) {
    return (
      <div className="diff-view">
        {processedContent.map((line, index) => {
            // 增强版检测 mermaid 图表逻辑
          const isMermaid =
            line.content &&
              (
                // 常规Mermaid标记
                line.content.includes("```mermaid") ||
                line.content.includes("\"```mermaid") ||
                
                // 不同类型的Mermaid图表
                /quadrantChart|classDiagram|sequenceDiagram|flowchart|graph|gantt|requirementDiagram|C4Context|erDiagram|stateDiagram|pie|journey/i.test(line.content) ||
                
                // 引号中的Mermaid内容
                /["'`].*?mermaid.*?["'`]/i.test(line.content)
              )

            // 获取Mermaid图表前的内容
            const getMermaidPrefix = (content) => {
              if (!content) return "";
              
              if (content.includes("```mermaid")) {
                const index = content.indexOf("```mermaid");
                return content.substring(0, index);
              } else if (content.includes("\"```mermaid")) {
                const index = content.indexOf("\"```mermaid");
                return content.substring(0, index);
              } else {
                // 如果没有找到标记，则返回前40个字符
                return content.substring(0, Math.min(40, content.length)) + "...";
              }
            }

          // 处理 mermaid 内容
          const getMermaidContent = (content) => {
              if (!content) return "";
              
              try {
                // 检查是否包含Mermaid图表标记
                if (!content.includes("```mermaid")) {
                  return "";
                }
                
                // 定位mermaid标记的开始位置
                const mermaidStartIndex = content.indexOf("```mermaid");
                if (mermaidStartIndex === -1) return "";
                
                // 查找结束标记
                const mermaidEndIndex = content.indexOf("```", mermaidStartIndex + 10);
                if (mermaidEndIndex === -1) return "";
                
                // 提取mermaid内容，不包括标记
                let chartContent = content.substring(mermaidStartIndex + 10, mermaidEndIndex).trim();
                
                // 使用共享函数处理内容
                return processMermaidContent(chartContent);
              } catch (error) {
                console.error("Error extracting Mermaid content:", error);
                return "";
              }
            }

            // 新增函数: 处理差异数据中的Mermaid图表
            // 扫描当前行周围的内容，合并完整的图表
            const mergeMermaidContent = (currentIndex) => {
              // 如果当前行被删除，不渲染图表
              if (line.type === "deleted") return "";
              
              // 寻找连续的Mermaid内容块
              let mermaidBlock = line.content;
              let context = "";
              
              // 判断是否找到完整Mermaid块（包含开始和结束标记）
              const isCompleteMermaid = (text) => {
                return text.includes("```mermaid") && 
                       text.includes("```", text.indexOf("```mermaid") + 10);
              };
              
              // 提取当前行中的图表内容
              if (isCompleteMermaid(mermaidBlock)) {
                // 如果当前行已经包含完整的Mermaid图表，直接处理
                return getMermaidContent(mermaidBlock);
              }
              
              // 图表可能跨越多行，需要向前后查找
              // 1. 首先向前查找合并有效内容
              let prevIndex = currentIndex - 1;
              let foundStart = line.content.includes("```mermaid");
              
              // 如果当前行不包含开始标记，向前查找
              if (!foundStart) {
                while (prevIndex >= 0) {
                  const prevLine = processedContent[prevIndex];
                  
                  // 跳过已删除的行
                  if (prevLine.type === "deleted") {
                    prevIndex--;
                    continue;
                  }
                  
                  // 判断是否找到起始标记
                  if (prevLine.content && prevLine.content.includes("```mermaid")) {
                    foundStart = true;
                    context = prevLine.content + "\n" + context;
                    break;
                  }
                  
                  // 采集可能的前置内容
                  if (prevLine.content) {
                    context = prevLine.content + "\n" + context;
                  }
                  
                  prevIndex--;
                }
              }
              
              // 2. 然后向后查找合并有效内容
              context += line.content;
              let nextIndex = currentIndex + 1;
              let foundEnd = line.content.includes("```", line.content.indexOf("```mermaid") + 10);
              
              // 如果尚未找到结束标记，向后查找
              if (!foundEnd && nextIndex < processedContent.length) {
                while (nextIndex < processedContent.length) {
                  const nextLine = processedContent[nextIndex];
                  
                  // 跳过已删除的行
                  if (nextLine.type === "deleted") {
                    nextIndex++;
                    continue;
                  }
                  
                  // 添加到上下文
                  if (nextLine.content) {
                    context += "\n" + nextLine.content;
                  }
                  
                  // 判断是否已包含结束标记
                  if (nextLine.content && nextLine.content.includes("```")) {
                    foundEnd = true;
                    break;
                  }
                  
                  nextIndex++;
                }
              }
              
              // 如果成功收集到完整的Mermaid块，处理它
              if (foundStart && (foundEnd || context.includes("```", context.indexOf("```mermaid") + 10))) {
                return getMermaidContent(context);
              }
              
              // 如果无法构建完整的Mermaid块，使用当前行内容进行最佳尝试
              return getMermaidContent(line.content);
            };

            // 新增函数：同时合并新旧两个版本的Mermaid图表
            const getDiffMermaidContent = () => {
              // 如果当前行已被处理为Mermaid图表的一部分，则跳过
              if (processedMermaidIndicesRef.current.has(index)) {
                return null;
              }
              
              // 检查当前行是否与Mermaid图表相关
              if (!isMermaid) return null;
              
              // 收集旧版本内容（deleted + unchanged）和新版本内容（added + unchanged）
              let oldContent = "";
              let newContent = "";
              
              // 特殊处理：检查当前行是否是完整的被删除/添加的Mermaid图表
              // 这通常发生在整个图表被替换的情况下
              if (line.type === "deleted" && line.content && line.content.includes("```mermaid")) {
                // 尝试提取完整的Mermaid块
                const start = line.content.indexOf("```mermaid");
                let end = line.content.indexOf("```", start + 10);
                
                if (end !== -1) {
                  // 当前行是一个完整的被删除的Mermaid图表
                  oldContent = line.content.substring(start, end + 3);
                  
                  // 查找对应的新增图表（通常紧跟在删除行之后）
                  for (let i = index + 1; i < processedContent.length; i++) {
                    const nextLine = processedContent[i];
                    if (nextLine.type === "added" && nextLine.content && nextLine.content.includes("```mermaid")) {
                      const addedStart = nextLine.content.indexOf("```mermaid");
                      let addedEnd = nextLine.content.indexOf("```", addedStart + 10);
                      
                      if (addedEnd !== -1) {
                        newContent = nextLine.content.substring(addedStart, addedEnd + 3);
                        break;
                      }
                    }
                  }
                  
                  // 处理收集到的内容
                  const oldChart = oldContent.trim() ? getMermaidContent(oldContent) : null;
                  const newChart = newContent.trim() ? getMermaidContent(newContent) : null;
                  
                  // 确保提取的Mermaid内容是有效的
                  if (oldChart && oldChart.startsWith("quadrantChart")) {
                    return {
                      old: oldChart,
                      new: newChart,
                      hasDiff: true // 完整替换必然存在差异
                    };
                  }
                }
              }
              
              if (line.type === "added" && line.content && line.content.includes("```mermaid")) {
                // 尝试提取完整的Mermaid块
                const start = line.content.indexOf("```mermaid");
                let end = line.content.indexOf("```", start + 10);
                
                if (end !== -1) {
                  // 当前行是一个完整的新增Mermaid图表
                  newContent = line.content.substring(start, end + 3);
                  
                  // 查找对应的删除图表（通常在新增行之前）
                  for (let i = index - 1; i >= 0; i--) {
                    const prevLine = processedContent[i];
                    if (prevLine.type === "deleted" && prevLine.content && prevLine.content.includes("```mermaid")) {
                      const deletedStart = prevLine.content.indexOf("```mermaid");
                      let deletedEnd = prevLine.content.indexOf("```", deletedStart + 10);
                      
                      if (deletedEnd !== -1) {
                        oldContent = prevLine.content.substring(deletedStart, deletedEnd + 3);
                        break;
                      }
                    }
                  }
                  
                  // 处理收集到的内容
                  const oldChart = oldContent.trim() ? getMermaidContent(oldContent) : null;
                  const newChart = newContent.trim() ? getMermaidContent(newContent) : null;
                  
                  // 确保提取的Mermaid内容是有效的
                  if (newChart && newChart.startsWith("quadrantChart")) {
                    return {
                      old: oldChart,
                      new: newChart,
                      hasDiff: true // 完整替换必然存在差异
                    };
                  }
                }
              }
              
              // 下面是常规的图表处理逻辑（针对部分修改的图表）
              // 找到当前行所属的Mermaid图表范围
              // 首先确定当前行是否是图表的开始行，或者是图表中间/结束的行
              let isStartLine = line.content && line.content.includes("```mermaid");
              let currentMermaidStartIndex = -1;
              let currentMermaidEndIndex = -1;
              
              // 如果当前行是图表的开始行
              if (isStartLine) {
                currentMermaidStartIndex = index;
              } else {
                // 向前查找最近的图表开始行
                for (let i = index - 1; i >= 0; i--) {
                  const prevLine = processedContent[i];
                  if (prevLine.content && prevLine.content.includes("```mermaid")) {
                    currentMermaidStartIndex = i;
                    break;
                  }
                  
                  // 如果找到了另一个图表的结束标记，说明当前行不属于任何图表
                  if (prevLine.content && prevLine.content.includes("```") && 
                      !prevLine.content.includes("```mermaid")) {
                    break;
                  }
                }
              }
              
              // 如果没有找到当前行所属的图表开始位置，返回null
              if (currentMermaidStartIndex === -1) return null;
              
              // 从图表开始位置向后查找图表结束位置
              for (let i = currentMermaidStartIndex + 1; i < processedContent.length; i++) {
                const nextLine = processedContent[i];
                // 找到图表结束标记
                if (nextLine.content && 
                    !nextLine.content.includes("```mermaid") && 
                    nextLine.content.includes("```")) {
                  currentMermaidEndIndex = i;
                  break;
                }
                
                // 如果找到了另一个图表的开始标记，说明前一个图表没有正确结束
                if (i > currentMermaidStartIndex && nextLine.content && nextLine.content.includes("```mermaid")) {
                  // 将当前位置作为前一个图表的结束位置
                  currentMermaidEndIndex = i - 1;
                  break;
                }
              }
              
              // 如果没有找到结束标记，使用最后一行
              if (currentMermaidEndIndex === -1) {
                currentMermaidEndIndex = processedContent.length - 1;
              }
              
              // 收集所有被删除的行（构建旧版本内容）
              let deletedChartContent = "";
              let addedChartContent = "";
              let hasCollectedDeletedContent = false;
              let hasCollectedAddedContent = false;
              
              // 首先查找图表区域内是否有完整的deleted或added的图表
              for (let i = currentMermaidStartIndex; i <= currentMermaidEndIndex; i++) {
                const curLine = processedContent[i];
                
                // 收集被删除的完整图表
                if (curLine.type === "deleted" && curLine.content && 
                    curLine.content.includes("```mermaid") && 
                    curLine.content.includes("```", curLine.content.indexOf("```mermaid") + 10)) {
                  deletedChartContent = curLine.content;
                  hasCollectedDeletedContent = true;
                }
                
                // 收集新增的完整图表
                if (curLine.type === "added" && curLine.content && 
                    curLine.content.includes("```mermaid") && 
                    curLine.content.includes("```", curLine.content.indexOf("```mermaid") + 10)) {
                  addedChartContent = curLine.content;
                  hasCollectedAddedContent = true;
                }
              }
              
              // 如果没有找到完整的删除/新增图表，则逐行收集
              if (!hasCollectedDeletedContent || !hasCollectedAddedContent) {
                // 只收集当前图表范围内的新旧版本内容
                for (let i = currentMermaidStartIndex; i <= currentMermaidEndIndex; i++) {
                  // 确保索引在content范围内
                  if (i >= 0 && i < processedContent.length) {
                    const curLine = processedContent[i];
                    
                    // 旧版本：包含未更改的行和已删除的行
                    if (!hasCollectedDeletedContent && (curLine.type === "unchanged" || curLine.type === "deleted")) {
                      oldContent += curLine.content + "\n";
                    }
                    
                    // 新版本：包含未更改的行和新增的行
                    if (!hasCollectedAddedContent && (curLine.type === "unchanged" || curLine.type === "added")) {
                      newContent += curLine.content + "\n";
                    }
                  }
                }
              } else {
                // 使用已收集的完整图表内容
                oldContent = deletedChartContent;
                newContent = addedChartContent;
              }
              
              // 处理收集到的内容
              const oldChart = oldContent.trim() ? getMermaidContent(oldContent) : null;
              const newChart = newContent.trim() ? getMermaidContent(newContent) : null;
              
              // 当成功识别到Mermaid图表时，标记其所有行为已处理
              if (currentMermaidStartIndex !== -1 && currentMermaidEndIndex !== -1) {
                for (let i = currentMermaidStartIndex; i <= currentMermaidEndIndex; i++) {
                  processedMermaidIndicesRef.current.add(i);
                }
              }
              
              // 返回处理后的新旧图表内容
              return {
                old: oldChart,
                new: newChart,
                hasDiff: oldChart !== newChart  // 检查新旧内容是否有差异
              };
            };
            
            // 获取差异图表内容
            const diffCharts = getDiffMermaidContent();
            
            // 添加样式以区分新旧图表
            const versionLabelStyle = {
              padding: '5px 10px',
              borderRadius: '4px',
              display: 'inline-block',
              margin: '5px 0',
              fontWeight: 'bold',
            };
            
            const oldVersionStyle = {
              ...versionLabelStyle,
              backgroundColor: 'rgba(220, 53, 69, 0.2)',
              color: '#dc3545',
              border: '1px solid #dc3545'
            };
            
            const newVersionStyle = {
              ...versionLabelStyle,
              backgroundColor: 'rgba(40, 167, 69, 0.2)',
              color: '#28a745',
              border: '1px solid #28a745'
            };

          return (
            <div key={`diff_line_${index}_${line.type}_${line.content}`}>
              <div
                className={`diff-line ${line.type} ${
                  selectedLines.includes(index) ? "selected-line" : ""
                }`}
                onClick={() => onLineClick(index)}
              >
                <span className="line-number">{index + 1}</span>
                <span className={`diff-marker ${line.type}`}>
                  {line.type === "unchanged"
                    ? " "
                    : line.type === "deleted"
                    ? "-"
                    : "+"}
                </span>
                  {isMermaid && line.type !== "deleted" ? (
                    diffCharts && diffCharts.hasDiff ? (
                      // 如果有差异，显示新旧两个版本的图表
                      <div className="diff-content mermaid-diff-content">
                        <span>{getMermaidPrefix(line.content)}</span>
                        
                        {/* 显示旧版本图表 */}
                        {diffCharts.old && (
                          <div className="old-mermaid-container" style={{ marginBottom: '20px', padding: '10px', border: '1px dashed #dc3545', borderRadius: '4px' }}>
                            <div style={oldVersionStyle}>旧版本图表</div>
                            <Suspense fallback={<div>Loading...</div>}>
                              <LazyMermaidRenderer chart={diffCharts.old} />
                            </Suspense>
                          </div>
                        )}
                        
                        {/* 显示新版本图表 */}
                        {diffCharts.new && (
                          <div className="new-mermaid-container" style={{ padding: '10px', border: '1px dashed #28a745', borderRadius: '4px' }}>
                            <div style={newVersionStyle}>新版本图表</div>
                            <Suspense fallback={<div>Loading...</div>}>
                              <LazyMermaidRenderer chart={diffCharts.new} />
                            </Suspense>
                          </div>
                        )}
                      </div>
                    ) : (
                      // 如果没有差异，只显示一个图表
                  <div className="diff-content mermaid-content">
                        <span>{getMermaidPrefix(line.content)}</span>
                        <Suspense fallback={<div>Loading...</div>}>
                          <LazyMermaidRenderer chart={mergeMermaidContent(index)} />
                        </Suspense>
                  </div>
                    )
                ) : (
                  <span className="diff-content">{line.content}</span>
                )}
              </div>
                {selectedLines.includes(index) && comments && (
                <div className="comment-input">
                  <CommentInput
                    index={index}
                    comment={comments[index] || ""}
                    onCommentChange={onCommentChange}
                  />
                </div>
              )}
            </div>
          )
          })}
        </div>
      )
    }
    
    // 处理包含表格的内容
    let currentIndex = 0
    const segments = []
    
    // 分割内容为表格和非表格区域
    tableGroups.forEach(({ start, end }) => {
      // 添加表格前的内容
      if (start > currentIndex) {
        segments.push({
          type: 'regular',
          content: processedContent.slice(currentIndex, start),
          startIndex: currentIndex,
          endIndex: start - 1
        })
      }
      
      // 添加表格内容
      segments.push({
        type: 'table',
        content: processedContent.slice(start, end + 1),
        startIndex: start,
        endIndex: end
      })
      
      currentIndex = end + 1
    })
    
    // 添加最后一个表格后的内容
    if (currentIndex < processedContent.length) {
      segments.push({
        type: 'regular',
        content: processedContent.slice(currentIndex),
        startIndex: currentIndex,
        endIndex: processedContent.length - 1
      })
    }
    
    return (
      <div className="diff-view">
        {segments.map((segment, segmentIndex) => {
          if (segment.type === 'table') {
            const tableLines = mergeTableContent(segment.content)
            const tableData = parseTable(tableLines)
            
            return (
              <div key={`table_segment_${segmentIndex}`} className="table-container">
                <TableRenderer tableData={tableData} />
              </div>
            )
          } else {
            // 渲染普通内容（非表格）
            return segment.content.map((line, lineOffset) => {
              const index = segment.startIndex + lineOffset
              
              // 增强版检测 mermaid 图表逻辑
              const isMermaid =
                line.content &&
                (
                  // 常规Mermaid标记
                  line.content.includes("```mermaid") ||
                  line.content.includes("\"```mermaid") ||
                  
                  // 不同类型的Mermaid图表
                  /quadrantChart|classDiagram|sequenceDiagram|flowchart|graph|gantt|requirementDiagram|C4Context|erDiagram|stateDiagram|pie|journey/i.test(line.content) ||
                  
                  // 引号中的Mermaid内容
                  /["'`].*?mermaid.*?["'`]/i.test(line.content)
                )

              // 获取Mermaid图表前的内容
              const getMermaidPrefix = (content) => {
                if (!content) return "";
                
                if (content.includes("```mermaid")) {
                  const index = content.indexOf("```mermaid");
                  return content.substring(0, index);
                } else if (content.includes("\"```mermaid")) {
                  const index = content.indexOf("\"```mermaid");
                  return content.substring(0, index);
                } else {
                  // 如果没有找到标记，则返回前40个字符
                  return content.substring(0, Math.min(40, content.length)) + "...";
                }
              }

              // 处理 mermaid 内容
              const getMermaidContent = (content) => {
                if (!content) return "";
                
                try {
                  // 检查是否包含Mermaid图表标记
                  if (!content.includes("```mermaid")) {
                    return "";
                  }
                  
                  // 定位mermaid标记的开始位置
                  const mermaidStartIndex = content.indexOf("```mermaid");
                  if (mermaidStartIndex === -1) return "";
                  
                  // 查找结束标记
                  const mermaidEndIndex = content.indexOf("```", mermaidStartIndex + 10);
                  if (mermaidEndIndex === -1) return "";
                  
                  // 提取mermaid内容，不包括标记
                  let chartContent = content.substring(mermaidStartIndex + 10, mermaidEndIndex).trim();
                  
                  // 使用共享函数处理内容
                  return processMermaidContent(chartContent);
                } catch (error) {
                  console.error("Error extracting Mermaid content:", error);
                  return "";
                }
              }

              // 新增函数: 处理差异数据中的Mermaid图表
              // 扫描当前行周围的内容，合并完整的图表
              const mergeMermaidContent = (currentIndex) => {
                // 如果当前行被删除，不渲染图表
                if (line.type === "deleted") return "";
                
                // 寻找连续的Mermaid内容块
                let mermaidBlock = line.content;
                let context = "";
                
                // 判断是否找到完整Mermaid块（包含开始和结束标记）
                const isCompleteMermaid = (text) => {
                  return text.includes("```mermaid") && 
                         text.includes("```", text.indexOf("```mermaid") + 10);
                };
                
                // 提取当前行中的图表内容
                if (isCompleteMermaid(mermaidBlock)) {
                  // 如果当前行已经包含完整的Mermaid图表，直接处理
                  return getMermaidContent(mermaidBlock);
                }
                
                // 图表可能跨越多行，需要向前后查找
                // 1. 首先向前查找合并有效内容
                let prevIndex = currentIndex - 1;
                let foundStart = line.content.includes("```mermaid");
                
                // 如果当前行不包含开始标记，向前查找
                if (!foundStart) {
                  while (prevIndex >= segment.startIndex) {
                    const prevLine = segment.content[prevIndex - segment.startIndex];
                    
                    // 跳过已删除的行
                    if (prevLine.type === "deleted") {
                      prevIndex--;
                      continue;
                    }
                    
                    // 判断是否找到起始标记
                    if (prevLine.content && prevLine.content.includes("```mermaid")) {
                      foundStart = true;
                      context = prevLine.content + "\n" + context;
                      break;
                    }
                    
                    // 采集可能的前置内容
                    if (prevLine.content) {
                      context = prevLine.content + "\n" + context;
                    }
                    
                    prevIndex--;
                  }
                }
                
                // 2. 然后向后查找合并有效内容
                context += line.content;
                let nextIndex = currentIndex + 1;
                let foundEnd = line.content.includes("```", line.content.indexOf("```mermaid") + 10);
                
                // 如果尚未找到结束标记，向后查找
                if (!foundEnd && nextIndex <= segment.endIndex) {
                  while (nextIndex <= segment.endIndex) {
                    const nextLine = segment.content[nextIndex - segment.startIndex];
                    
                    // 跳过已删除的行
                    if (nextLine.type === "deleted") {
                      nextIndex++;
                      continue;
                    }
                    
                    // 添加到上下文
                    if (nextLine.content) {
                      context += "\n" + nextLine.content;
                    }
                    
                    // 判断是否已包含结束标记
                    if (nextLine.content && nextLine.content.includes("```")) {
                      foundEnd = true;
                      break;
                    }
                    
                    nextIndex++;
                  }
                }
                
                // 如果成功收集到完整的Mermaid块，处理它
                if (foundStart && (foundEnd || context.includes("```", context.indexOf("```mermaid") + 10))) {
                  return getMermaidContent(context);
                }
                
                // 如果无法构建完整的Mermaid块，使用当前行内容进行最佳尝试
                return getMermaidContent(line.content);
              };

              // 新增函数：同时合并新旧两个版本的Mermaid图表
              const getDiffMermaidContent = () => {
                // 如果当前行已被处理为Mermaid图表的一部分，则跳过
                if (processedMermaidIndicesRef.current.has(index)) {
                  return null;
                }
                
                // 检查当前行是否与Mermaid图表相关
                if (!isMermaid) return null;
                
                // 收集旧版本内容（deleted + unchanged）和新版本内容（added + unchanged）
                let oldContent = "";
                let newContent = "";
                
                // 特殊处理：检查当前行是否是完整的被删除/添加的Mermaid图表
                // 这通常发生在整个图表被替换的情况下
                if (line.type === "deleted" && line.content && line.content.includes("```mermaid")) {
                  // 尝试提取完整的Mermaid块
                  const start = line.content.indexOf("```mermaid");
                  let end = line.content.indexOf("```", start + 10);
                  
                  if (end !== -1) {
                    // 当前行是一个完整的被删除的Mermaid图表
                    oldContent = line.content.substring(start, end + 3);
                    
                    // 查找对应的新增图表（通常紧跟在删除行之后）
                    for (let i = index + 1; i < processedContent.length; i++) {
                      const nextLine = processedContent[i];
                      if (nextLine.type === "added" && nextLine.content && nextLine.content.includes("```mermaid")) {
                        const addedStart = nextLine.content.indexOf("```mermaid");
                        let addedEnd = nextLine.content.indexOf("```", addedStart + 10);
                        
                        if (addedEnd !== -1) {
                          newContent = nextLine.content.substring(addedStart, addedEnd + 3);
                          break;
                        }
                      }
                    }
                    
                    // 处理收集到的内容
                    const oldChart = oldContent.trim() ? getMermaidContent(oldContent) : null;
                    const newChart = newContent.trim() ? getMermaidContent(newContent) : null;
                    
                    // 确保提取的Mermaid内容是有效的
                    if (oldChart && oldChart.startsWith("quadrantChart")) {
                      return {
                        old: oldChart,
                        new: newChart,
                        hasDiff: true // 完整替换必然存在差异
                      };
                    }
                  }
                }
                
                if (line.type === "added" && line.content && line.content.includes("```mermaid")) {
                  // 尝试提取完整的Mermaid块
                  const start = line.content.indexOf("```mermaid");
                  let end = line.content.indexOf("```", start + 10);
                  
                  if (end !== -1) {
                    // 当前行是一个完整的新增Mermaid图表
                    newContent = line.content.substring(start, end + 3);
                    
                    // 查找对应的删除图表（通常在新增行之前）
                    for (let i = index - 1; i >= 0; i--) {
                      const prevLine = processedContent[i];
                      if (prevLine.type === "deleted" && prevLine.content && prevLine.content.includes("```mermaid")) {
                        const deletedStart = prevLine.content.indexOf("```mermaid");
                        let deletedEnd = prevLine.content.indexOf("```", deletedStart + 10);
                        
                        if (deletedEnd !== -1) {
                          oldContent = prevLine.content.substring(deletedStart, deletedEnd + 3);
                          break;
                        }
                      }
                    }
                    
                    // 处理收集到的内容
                    const oldChart = oldContent.trim() ? getMermaidContent(oldContent) : null;
                    const newChart = newContent.trim() ? getMermaidContent(newContent) : null;
                    
                    // 确保提取的Mermaid内容是有效的
                    if (newChart && newChart.startsWith("quadrantChart")) {
                      return {
                        old: oldChart,
                        new: newChart,
                        hasDiff: true // 完整替换必然存在差异
                      };
                    }
                  }
                }
                
                // 下面是常规的图表处理逻辑（针对部分修改的图表）
                // 找到当前行所属的Mermaid图表范围
                // 首先确定当前行是否是图表的开始行，或者是图表中间/结束的行
                let isStartLine = line.content && line.content.includes("```mermaid");
                let currentMermaidStartIndex = -1;
                let currentMermaidEndIndex = -1;
                
                // 如果当前行是图表的开始行
                if (isStartLine) {
                  currentMermaidStartIndex = index;
                } else {
                  // 向前查找最近的图表开始行
                  for (let i = index - 1; i >= segment.startIndex; i--) {
                    const lineOffset = i - segment.startIndex;
                    if (lineOffset >= 0 && lineOffset < segment.content.length) {
                      const prevLine = segment.content[lineOffset];
                      if (prevLine.content && prevLine.content.includes("```mermaid")) {
                        currentMermaidStartIndex = i;
                        break;
                      }
                      
                      // 如果找到了另一个图表的结束标记，说明当前行不属于任何图表
                      if (prevLine.content && prevLine.content.includes("```") && 
                          !prevLine.content.includes("```mermaid")) {
                        break;
                      }
                    }
                  }
                }
                
                // 如果没有找到当前行所属的图表开始位置，返回null
                if (currentMermaidStartIndex === -1) return null;
                
                // 从图表开始位置向后查找图表结束位置
                for (let i = currentMermaidStartIndex + 1; i <= segment.endIndex; i++) {
                  const lineOffset = i - segment.startIndex;
                  if (lineOffset >= 0 && lineOffset < segment.content.length) {
                    const nextLine = segment.content[lineOffset];
                    // 找到图表结束标记
                    if (nextLine.content && 
                        !nextLine.content.includes("```mermaid") && 
                        nextLine.content.includes("```")) {
                      currentMermaidEndIndex = i;
                      break;
                    }
                    
                    // 如果找到了另一个图表的开始标记，说明前一个图表没有正确结束
                    if (i > currentMermaidStartIndex && nextLine.content && nextLine.content.includes("```mermaid")) {
                      // 将当前位置作为前一个图表的结束位置
                      currentMermaidEndIndex = i - 1;
                      break;
                    }
                  }
                }
                
                // 如果没有找到结束标记，使用段落的最后一行
                if (currentMermaidEndIndex === -1) {
                  currentMermaidEndIndex = segment.endIndex;
                }
                
                // 收集所有被删除的行（构建旧版本内容）
                let deletedChartContent = "";
                let addedChartContent = "";
                let hasCollectedDeletedContent = false;
                let hasCollectedAddedContent = false;
                
                // 首先查找图表区域内是否有完整的deleted或added的图表
                for (let i = currentMermaidStartIndex; i <= currentMermaidEndIndex; i++) {
                  const lineOffset = i - segment.startIndex;
                  if (lineOffset >= 0 && lineOffset < segment.content.length) {
                    const curLine = segment.content[lineOffset];
                    
                    // 收集被删除的完整图表
                    if (curLine.type === "deleted" && curLine.content && 
                        curLine.content.includes("```mermaid") && 
                        curLine.content.includes("```", curLine.content.indexOf("```mermaid") + 10)) {
                      deletedChartContent = curLine.content;
                      hasCollectedDeletedContent = true;
                    }
                    
                    // 收集新增的完整图表
                    if (curLine.type === "added" && curLine.content && 
                        curLine.content.includes("```mermaid") && 
                        curLine.content.includes("```", curLine.content.indexOf("```mermaid") + 10)) {
                      addedChartContent = curLine.content;
                      hasCollectedAddedContent = true;
                    }
                  }
                }
                
                // 如果没有找到完整的删除/新增图表，则逐行收集
                if (!hasCollectedDeletedContent || !hasCollectedAddedContent) {
                  // 只收集当前图表范围内的新旧版本内容
                  for (let i = currentMermaidStartIndex; i <= currentMermaidEndIndex; i++) {
                    const lineOffset = i - segment.startIndex;
                    // 确保索引在segment.content范围内
                    if (lineOffset >= 0 && lineOffset < segment.content.length) {
                      const curLine = segment.content[lineOffset];
                      
                      // 旧版本：包含未更改的行和已删除的行
                      if (!hasCollectedDeletedContent && (curLine.type === "unchanged" || curLine.type === "deleted")) {
                        oldContent += curLine.content + "\n";
                      }
                      
                      // 新版本：包含未更改的行和新增的行
                      if (!hasCollectedAddedContent && (curLine.type === "unchanged" || curLine.type === "added")) {
                        newContent += curLine.content + "\n";
                      }
                    }
                  }
                } else {
                  // 使用已收集的完整图表内容
                  oldContent = deletedChartContent;
                  newContent = addedChartContent;
                }
                
                // 处理收集到的内容
                const oldChart = oldContent.trim() ? getMermaidContent(oldContent) : null;
                const newChart = newContent.trim() ? getMermaidContent(newContent) : null;
                
                // 当成功识别到Mermaid图表时，标记其所有行为已处理
                if (currentMermaidStartIndex !== -1 && currentMermaidEndIndex !== -1) {
                  for (let i = currentMermaidStartIndex; i <= currentMermaidEndIndex; i++) {
                    processedMermaidIndicesRef.current.add(i);
                  }
                }
                
                // 返回处理后的新旧图表内容
                return {
                  old: oldChart,
                  new: newChart,
                  hasDiff: oldChart !== newChart  // 检查新旧内容是否有差异
                };
              };
              
              // 获取差异图表内容
              const diffCharts = getDiffMermaidContent();
              
              // 添加样式以区分新旧图表
              const versionLabelStyle = {
                padding: '5px 10px',
                borderRadius: '4px',
                display: 'inline-block',
                margin: '5px 0',
                fontWeight: 'bold',
              };
              
              const oldVersionStyle = {
                ...versionLabelStyle,
                backgroundColor: 'rgba(220, 53, 69, 0.2)',
                color: '#dc3545',
                border: '1px solid #dc3545'
              };
              
              const newVersionStyle = {
                ...versionLabelStyle,
                backgroundColor: 'rgba(40, 167, 69, 0.2)',
                color: '#28a745',
                border: '1px solid #28a745'
              };

              return (
                <div key={`diff_line_${index}_${line.type}_${line.content}`}>
                  <div
                    className={`diff-line ${line.type} ${
                      selectedLines.includes(index) ? "selected-line" : ""
                    }`}
                    onClick={() => onLineClick(index)}
                  >
                    <span className="line-number">{index + 1}</span>
                    <span className={`diff-marker ${line.type}`}>
                      {line.type === "unchanged"
                        ? " "
                        : line.type === "deleted"
                        ? "-"
                        : "+"}
                    </span>
                    {isMermaid && line.type !== "deleted" ? (
                      diffCharts && diffCharts.hasDiff ? (
                        // 如果有差异，显示新旧两个版本的图表
                        <div className="diff-content mermaid-diff-content">
                          <span>{getMermaidPrefix(line.content)}</span>
                          
                          {/* 显示旧版本图表 */}
                          {diffCharts.old && (
                            <div className="old-mermaid-container" style={{ marginBottom: '20px', padding: '10px', border: '1px dashed #dc3545', borderRadius: '4px' }}>
                              <div style={oldVersionStyle}>旧版本图表</div>
                              <Suspense fallback={<div>Loading...</div>}>
                                <LazyMermaidRenderer chart={diffCharts.old} />
                              </Suspense>
                            </div>
                          )}
                          
                          {/* 显示新版本图表 */}
                          {diffCharts.new && (
                            <div className="new-mermaid-container" style={{ padding: '10px', border: '1px dashed #28a745', borderRadius: '4px' }}>
                              <div style={newVersionStyle}>新版本图表</div>
                              <Suspense fallback={<div>Loading...</div>}>
                                <LazyMermaidRenderer chart={diffCharts.new} />
                              </Suspense>
                            </div>
                          )}
                        </div>
                      ) : (
                        // 如果没有差异，只显示一个图表
                        <div className="diff-content mermaid-content">
                          <span>{getMermaidPrefix(line.content)}</span>
                          <Suspense fallback={<div>Loading...</div>}>
                            <LazyMermaidRenderer chart={mergeMermaidContent(index)} />
                          </Suspense>
                        </div>
                      )
                    ) : (
                      <span className="diff-content">{line.content}</span>
                    )}
                  </div>
                  {selectedLines.includes(index) && comments && (
                    <div className="comment-input">
                      <CommentInput
                        index={index}
                        comment={comments[index] || ""}
                        onCommentChange={onCommentChange}
                      />
                    </div>
                  )}
                </div>
              )
            })
          }
        })}
      </div>
    )
  }

  // 现有的处理逻辑
  const formatJsonContent = (context) => {
    // 内存优化：如果内容过大，直接返回原始内容
    if (typeof context === 'string' && context.length > 5000000) {
      console.warn('Content too large, skipping formatting to avoid memory issues');
      return context;
    }

    // Helper function to determine if a string is JSON
    const isValidJson = (str) => {
      try {
        JSON.parse(str)
        return true
      } catch (error) {
        return false
      }
    }

    // Function to process and format the content
    const indent = (str, spaces) => str.replace(/^/gm, " ".repeat(spaces))

    const wrapDiagram = (content, type) => {
      // 内存优化：避免不必要的日志输出
      // console.log("content", content)
      
      // 修改: 更精确地处理转义字符，不再通用替换所有反斜杠
      const trimmedContent = content
        .replace(/\\n/g, "\n")    // 将 \n 转换为实际换行符
        .replace(/\\"/g, '"')     // 将 \" 转换为 "
        .replace(/\\'/g, "'")     // 将 \' 转换为 '
        .replace(/\\\\/g, "\\")   // 将 \\ 转换为 \
        // 移除: 不再替换所有反斜杠，这会破坏格式
        // .replace(/\\/g, " ")   // 这行会破坏Mermaid语法
        .trim();
      
      // 特殊处理quadrantChart格式
      if (type === "quadrantChart" || trimmedContent.includes("quadrantChart")) {
        // 格式化quadrantChart内容以确保正确的格式
        const formattedContent = formatQuadrantChart(trimmedContent);
        console.log("Formatted quadrantChart content:", formattedContent); // 调试用
        return `\n\`\`\`mermaid\n${formattedContent}\n\`\`\`\n`;
      }

      // 根据类型包装内容
      switch (type) {
        case "classDiagram":
          return `\n\`\`\`mermaid\n${
            trimmedContent.startsWith("classDiagram")
              ? trimmedContent
              : `classDiagram\n${trimmedContent}`
          }\n\`\`\`\n`
        case "sequenceDiagram":
          return `\n\`\`\`mermaid\n${
            trimmedContent.startsWith("sequenceDiagram")
              ? trimmedContent
              : `sequenceDiagram\n${trimmedContent}`
          }\n\`\`\`\n`
        case "requirementDiagram":
          return `\n\`\`\`mermaid\n${
            trimmedContent.startsWith("requirementDiagram")
              ? trimmedContent
              : `requirementDiagram\n${trimmedContent}`
          }\n\`\`\`\n`
        case "C4Context":
          return `\n\`\`\`mermaid\n${
            trimmedContent.startsWith("C4Context")
              ? trimmedContent
              : `C4Context\n${trimmedContent}`
          }\n\`\`\`\n`
        case " gantt":
          return `\n\`\`\`mermaid\n${
            trimmedContent.startsWith(" gantt")
              ? trimmedContent
              : ` gantt\n${trimmedContent}`
          }\n\`\`\`\n`
        case " graph TB":
          return `\n\`\`\`mermaid\n${
            trimmedContent.startsWith(" graph TB")
              ? trimmedContent
              : ` graph TB\n${trimmedContent}`
          }\n\`\`\`\n`
        case " flowchart TB":
          return `\n\`\`\`mermaid\n${
            trimmedContent.startsWith(" flowchart TB")
              ? trimmedContent
              : ` flowchart TB\n${trimmedContent}`
          }\n\`\`\`\n`
        default:
          if (!trimmedContent.startsWith("<mermaid>")) {
            return `\n\`\`\`mermaid\n${trimmedContent}\n\`\`\`\n`
          }
          return `\n${trimmedContent}\n`
      }
    }

    // 内存优化：限制递归深度
    const formatValue = (value, indentLevel = 0, depth = 0) => {
      // 防止过深递归
      if (depth > 20) {
        return JSON.stringify(value);
      }

      if (typeof value === "string") {
        // 新增检查：如果是Mermaid块，直接返回处理后的值，保留换行符
        // 这样可以避免被JSON.stringify转义或被三引号包裹
        if (value.includes("```mermaid")) {
          // 直接返回原始字符串，因为processSpecialFields已经处理了换行符
          // 后续的 split 会正确识别 ```mermaid 标记
          return value; 
        }

        // 保留原来的多行字符串处理逻辑（可能对其他字段有用）
        if (value.includes("\n")) {
          return (
            '"""\n' +
            indent(value, indentLevel + 2) +
            "\n" +
            " ".repeat(indentLevel) +
            '"""'
          )
        }
        // 普通单行字符串
        return JSON.stringify(value)
      }
      if (Array.isArray(value)) {
        if (value.length === 0) return "[]"
        if (
          value.every(
            (item) => typeof item === "string" && !item.includes("\n")
          )
        ) {
          return (
            "[" + value.map((item) => JSON.stringify(item)).join(", ") + "]"
          )
        }
        let result = "[\n"
        value.forEach((item, index) => {
          result += indent(formatValue(item, indentLevel + 2, depth + 1), indentLevel + 2)
          if (index < value.length - 1) result += ","
          result += "\n"
        })
        result += " ".repeat(indentLevel) + "]"
        return result
      }
      if (typeof value === "object" && value !== null) {
        return formatObject(value, indentLevel, depth + 1)
      }
      return JSON.stringify(value)
    }

    const formatObject = (obj, indentLevel = 0, depth = 0) => {
      // 防止过深递归
      if (depth > 20) {
        return JSON.stringify(obj);
      }

      if (Object.keys(obj).length === 0) return "{}"
      let result = "{\n"
      Object.entries(obj).forEach(([key, value], index, array) => {
        result += indent(
          `${JSON.stringify(key)}: ${formatValue(value, indentLevel + 2, depth + 1)}`,
          indentLevel + 2
        )
        if (index < array.length - 1) result += ","
        result += "\n"
      })
      result += " ".repeat(indentLevel) + "}"
      return result
    }

    // 内存优化：限制处理深度
    const processSpecialFields = (obj, depth = 0) => {
      // 防止过深递归
      if (depth > 20) {
        return obj;
      }

      for (const [key, value] of Object.entries(obj)) {
        if (typeof value === "string") {
          // 检查常规的Mermaid标记
          if (value.includes("```mermaid")) {
            // 已包含标准Mermaid标记，检查是否需要处理转义字符
            if (value.includes("\\n")) {
              obj[key] = value.replace(/\\n/g, "\n");
            }
            // 如果已经包含 ```mermaid，则不再进行后续的关键词检查
            continue;
          }
          
          // 检查转义形式的Mermaid标记 
          if (value.includes("\\n```mermaid")) {
            obj[key] = value.replace(/\\n/g, "\n");
            // 同样，如果已经识别为Mermaid块，不再进行后续关键词检查
            continue;
          }

          // 检查键名是否包含特定关键词 (四象限图)
          const keyLowerCase = key.toLowerCase();
          if (
            keyLowerCase.includes("quadrant") ||
            keyLowerCase.includes("四象限") || 
            keyLowerCase.includes("象限") ||  
            value.trim().startsWith("quadrantChart") || // 检查原始值
            value.includes("\\nquadrantChart") // 检查转义形式
          ) {
            // 1. 处理转义字符
            let processedValue = value.replace(/\\n/g, "\n");
            
            // 2. 确保Mermaid标记存在且正确
            const hasMermaidStart = processedValue.includes("```mermaid");
            const hasMermaidEnd = processedValue.lastIndexOf("```") > processedValue.indexOf("```mermaid");
            
            if (!hasMermaidStart) {
              processedValue = "```mermaid\n" + processedValue.trim();
            }
            // 只有当结束标记确实缺失时才添加
            if (!hasMermaidEnd) {
               processedValue = processedValue.trim() + "\n```";
            }
            
            // 3. 提取核心内容 (```mermaid 和 ``` 之间)
            let contentInsideMarkers = "";
            const startIndex = processedValue.indexOf("```mermaid") + 10; // after ```mermaid\n
            const endIndex = processedValue.lastIndexOf("```");
            if (endIndex > startIndex) {
              contentInsideMarkers = processedValue.substring(startIndex, endIndex).trim();
            } else if (processedValue.includes("```mermaid")) {
              // 处理只有开始标记的情况
              contentInsideMarkers = processedValue.substring(startIndex).trim();
            } else {
              // 处理完全没有标记的情况 (虽然前面加了，保险起见)
              contentInsideMarkers = processedValue.trim();
            }

            // 4. 检查核心内容是否以 quadrantChart 开头
            if (!contentInsideMarkers.startsWith("quadrantChart")) {
              // 5. 按需添加 quadrantChart
              // 在 ```mermaid\n 之后插入
              const insertPos = processedValue.indexOf("```mermaid") + 10; 
              processedValue = processedValue.slice(0, insertPos) + "quadrantChart\n" + contentInsideMarkers;
              // 确保结束标记存在
              if (!processedValue.endsWith("\n```")) {
                 if (processedValue.endsWith("```")) {
                    processedValue = processedValue.slice(0, -3) + "\n```";
                 } else {
                    processedValue += "\n```";
                 }
              }
            }
            
            obj[key] = processedValue; 
            // 处理完四象限图后，不再进行其他检查
            continue; 
          }
          
          // TODO: 如果有其他类型的图表需要特殊处理，可以在这里添加类似逻辑

        } else if (typeof value === "object" && value !== null) {
          obj[key] = processSpecialFields(value, depth + 1);
        }
      }
      return obj;
    }

    try {
      if (context) {
        // 内存优化：避免不必要的日志输出
        // console.log(context)
        // 如果 context 已经是对象，直接使用它
        const obj = typeof context === "string" ? JSON.parse(context) : context

        // console.log("处理的obj:", obj)
        const processedContent = processSpecialFields(obj)

        return formatObject(processedContent)
      } else {
        return context
      }
    } catch (error) {
      console.error("Error formatting JSON content:", error)
      return context || "" // Return the original content or empty string on error
    }
  }

  // 确保 content 是一个字符串
  const formattedContent = formatJsonContent(content)
  const parts =
    typeof formattedContent === "string"
      ? formattedContent.split(/(```mermaid[\s\S]*?```)/)
      : []
  
  // 内存优化：限制渲染的部分数量
  const maxParts = 50;
  const limitedParts = parts.length > maxParts ? parts.slice(0, maxParts) : parts;
  
  return (
    <div className={fileStyles.jsonContent}>
      {limitedParts.map((part, index) => {
        // 内存优化：避免不必要的日志输出
        // console.log("part", part)
        if (part.startsWith("```mermaid")) {
          const chartContent = part
            .replace(/```mermaid\n?/, "")
            .replace(/```$/, "")
            .replace(/\\n/g, "\n")
            // 修改: 移除会破坏格式的通用替换
            // .replace(/\\/g, "") // 这行会破坏Mermaid语法
            .replace(/\\"/g, '"')  // 更精确地替换转义引号
            .replace(/\\'/g, "'")  // 更精确地替换转义单引号
            .replace(/\\\\/g, "\\") // 处理转义的反斜杠
            .replace(/'/g, '"')
            .replace(/END/g, "end")
          
          // 检查是否是quadrantChart，如果是则进行特殊格式化
          if (chartContent.includes("quadrantChart")) {
            const formattedChart = formatQuadrantChart(chartContent);
            return (
              <div key={index} className={fileStyles.mermaidContainer}>
                <Suspense fallback={<div>Loading...</div>}>
                  <LazyMermaidRenderer chart={formattedChart} />
                </Suspense>
              </div>
            )
          }

          return (
            <div key={index} className={fileStyles.mermaidContainer}>
              <Suspense fallback={<div>Loading...</div>}>
                <LazyMermaidRenderer chart={chartContent} />
              </Suspense>
            </div>
          )
        } else if (part.trim()) {
          return (
            <SyntaxHighlighter
              key={index}
              language="json"
              style={vscDarkPlus}
              wrapLines={true}
              wrapLongLines={true}
              className={fileStyles.syntaxHighlighter}
              customStyle={{margin: '8px 0'}}
            >
              {part}
            </SyntaxHighlighter>
          )
        } else {
          return null; // 忽略空内容
        }
      })}
      {parts.length > maxParts && (
        <div style={{ padding: '10px', color: '#888', textAlign: 'center' }}>
          内容过大，仅显示部分内容以避免性能问题
        </div>
      )}
    </div>
  )
}

// 添加React.memo包装并导出，使用自定义比较函数避免不必要的重新渲染
export default React.memo(EnhancedJsonRenderer, (prevProps, nextProps) => {
  // 首先检查是否来自不同文件（如果组件中有fileName属性）
  if (prevProps.fileName !== nextProps.fileName) {
    return false; // 不同文件，始终重新渲染
  }
  
  // 检查内容是否深度相等
  const contentEqual = (() => {
    try {
      // 对数组类型进行特殊处理
      if (Array.isArray(prevProps.content) && Array.isArray(nextProps.content)) {
        if (prevProps.content.length !== nextProps.content.length) {
          return false;
        }
        // 对于大型数组，只比较前10个和后10个元素以提高性能
        if (prevProps.content.length > 20) {
          const prevStart = JSON.stringify(prevProps.content.slice(0, 10));
          const nextStart = JSON.stringify(nextProps.content.slice(0, 10));
          if (prevStart !== nextStart) return false;
          
          const prevEnd = JSON.stringify(prevProps.content.slice(-10));
          const nextEnd = JSON.stringify(nextProps.content.slice(-10));
          return prevEnd === nextEnd;
        }
        // 对于小型数组进行完整比较
        return JSON.stringify(prevProps.content) === JSON.stringify(nextProps.content);
      }
      
      // 对象类型
      if (typeof prevProps.content === 'object' && typeof nextProps.content === 'object') {
        return JSON.stringify(prevProps.content) === JSON.stringify(nextProps.content);
      }
      
      // 基本类型
      return prevProps.content === nextProps.content;
    } catch (e) {
      // 如果比较过程出错，默认认为不相等，确保安全渲染
      console.error("比较JSON内容时出错:", e);
      return false;
    }
  })();
  
  // 检查其他props
  const linesEqual = JSON.stringify(prevProps.selectedLines) === JSON.stringify(nextProps.selectedLines);
  const commentsEqual = JSON.stringify(prevProps.comments) === JSON.stringify(nextProps.comments);
  const onLineClickEqual = prevProps.onLineClick === nextProps.onLineClick;
  const onCommentChangeEqual = prevProps.onCommentChange === nextProps.onCommentChange;
  
  const areEqual = contentEqual && linesEqual && commentsEqual && onLineClickEqual && onCommentChangeEqual;
  
  // 输出调试信息
  //console.log(`[EnhancedJsonRenderer] Props比较: ${areEqual ? '相等' : '不相等'}`);
  
  // 如果所有关键属性都没变，则不重新渲染
  return areEqual;
});